import { useAutoRefillStore } from '../../../stores/useAutoRefillStore';
import { getIsImpersonating } from '../../../utils/session';

interface AutoRefillTermsProps {
  config: {
    customerServicePhone: string;
    customerServiceEmail: string;
  };
}

export function AutoRefillTerms({ config }: AutoRefillTermsProps) {
  const {
    isPatientAutoRefillEnabled,
    agreeToTerms,
    noAutoFills,
    autoFillCount,
    saving,
    saveSuccess,
    error,
    setPatientEnabled,
    setAgreeToTerms,
    saveAutoRefills,
  } = useAutoRefillStore();

  const impersonating = getIsImpersonating();
  const phone = config.customerServicePhone || window.mxAppConfig?.CustomerServicePhone || '';
  const term = window.mxAppConfig?.CustomerServiceTerm || 'Member Services';
  const clientName = window.mxAppConfig?.ClientName || 'our pharmacy';

  const saveDisabled = (isPatientAutoRefillEnabled && !agreeToTerms) || impersonating || saving;

  return (
    <div className="panel">
      {/* Terms copy */}
      <div id="auto-refill-terms" className="refill-terms">
        <h5 style={{ fontWeight: 'bold', marginLeft: '20px' }}>Auto-Refill Terms</h5>

        {!isPatientAutoRefillEnabled && (
          <p>
            You are not currently enrolled in our automatic refill service. Enrolling yourself, or
            your dependents for automatic refills authorizes {clientName} to fill your
            prescriptions, charge the Home Delivery co-payment(s) to your credit card and send the
            prescriptions to your shipping address. If you have questions related to the automatic
            refill service of your prescription benefits, please contact {term} at {phone}.
          </p>
        )}

        {isPatientAutoRefillEnabled && (
          <p>
            You are enrolled in our automatic refill service. By enrolling yourself, or your
            dependents for automatic refills, you have authorized {clientName} to fill your
            prescriptions, charge the Home Delivery co-payment(s) to your credit card and send the
            prescriptions to your shipping address. If you have questions related to the automatic
            refill service of your prescription benefits, please contact {term} at {phone}.
          </p>
        )}

        <p>
          Reminder: {clientName} is authorized to fill prescriptions with generic equivalents when
          available and permissible by the law, in accordance with your benefit plan requirements.
          If you request a brand name drug when your doctor permits the generic substitution, you
          may be responsible for paying the difference in cost between the brand name drug and the
          generic equivalent in addition to your co-payment. Refer to your plan benefit information
          for more details.
        </p>

        {!noAutoFills && (
          <div id="agree-terms" className="agree-autofill-terms">
            <h5 style={{ fontWeight: 'bold' }}>I Agree To The Terms</h5>
            <input
              type="checkbox"
              checked={agreeToTerms}
              disabled={!isPatientAutoRefillEnabled}
              onChange={e => setAgreeToTerms(e.target.checked)}
            />
          </div>
        )}
      </div>

      {/* Patient auto-refill toggle */}
      <div className="auto-refill-toggle">
        <h5>PATIENT AUTO-REFILL STATUS</h5>
        <div
          className="ats-switch switch-large"
          role="switch"
          aria-label="Patient Auto-Refill Status"
          aria-checked={isPatientAutoRefillEnabled}
          aria-disabled={impersonating}
          tabIndex={impersonating ? -1 : 0}
          onClick={() => { if (!impersonating) setPatientEnabled(!isPatientAutoRefillEnabled); }}
          onKeyDown={e => {
            if (impersonating) return;
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setPatientEnabled(!isPatientAutoRefillEnabled);
            }
          }}
          style={impersonating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <div
            className={`switch-animate ${isPatientAutoRefillEnabled ? 'switch-on' : 'switch-off'}`}
          >
            <span className="switch-left">On</span>
            <span className="knob"> </span>
            <span className="switch-right">Off</span>
          </div>
        </div>

        {!noAutoFills && (
          <h5 style={{ fontWeight: 'bolder' }}>
            You Have {autoFillCount} Medications Enrolled In Auto-Refill
          </h5>
        )}
      </div>

      {/* Save / feedback */}
      {!noAutoFills && (
        <div
          className="row clearfix auto-refill-submit"
          style={{ paddingRight: '10px' }}
          title="You Must Agree To Terms"
        >
          <button
            type="button"
            disabled={saveDisabled}
            className="btn btn-success pull-right review-order-btn mobile-activte-btn auto-refill-btn"
            onClick={() => void saveAutoRefills()}
          >
            SAVE
          </button>
        </div>
      )}

      {noAutoFills && (
        <div className="auto-refill-submit">
          <h3>This patient has no prescriptions available for auto-refill.</h3>
        </div>
      )}

      {/* Saving spinner */}
      {saving && (
        <div>
          <div className="spinner">
            <div className="rect1" />
            {' '}
            <div className="rect2" />
            {' '}
            <div className="rect3" />
            {' '}
            <div className="rect4" />
            {' '}
            <div className="rect5" />
            <h4>Saving Settings</h4>
          </div>
        </div>
      )}

      {/* Success banner */}
      {saveSuccess && (
        <div className="alert alert-success" role="alert">
          Your Auto-Refill Settings Have Been Saved.
        </div>
      )}

      {/* Error banner */}
      {error && !saving && (
        <div className="alert alert-danger" role="alert">
          There was a problem saving your auto-refill settings. Please contact {term} at {phone}.
        </div>
      )}
    </div>
  );
}
